import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Trash2, Users } from 'lucide-react';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Switch } from '../../../components/ui/Switch.jsx';
import { FileInput } from '../../../components/ui/FileInput.jsx';
import { petsApi } from '../../../api/pets.js';
import { ApiError } from '../../../api/client.js';
import { t } from '../../../lib/i18n.js';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif';

export function PetPhotosModal({ open, onClose, pet, customerId }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [withOwner, setWithOwner] = useState(false);
  const [isPrimary, setIsPrimary] = useState(false);

  const photos = (pet && pet.photos) || [];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'pets'] });

  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      if (caption) fd.append('caption', caption);
      fd.append('withOwner', String(withOwner));
      fd.append('isPrimary', String(isPrimary));
      return petsApi.uploadPhoto(pet._id, fd);
    },
    onSuccess: () => {
      toast.success('Foto caricata.');
      setFile(null); setCaption(''); setWithOwner(false); setIsPrimary(false);
      invalidate();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const deleteMutation = useMutation({
    mutationFn: (storageKey) => petsApi.deletePhoto(pet._id, storageKey),
    onSuccess: () => { toast.success('Foto rimossa.'); invalidate(); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  return (
    <Modal
      open={open}
      onClose={uploadMutation.isPending ? undefined : onClose}
      title={pet ? `Foto di ${pet.name}` : 'Foto'}
      size="lg"
      footer={<Button variant="ghost" onClick={onClose}>{t('common.close') || 'Chiudi'}</Button>}
    >
      <div className="flex flex-col gap-5">
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {photos.map((p) => (
              <figure key={p.storageKey || p.url} className="group relative overflow-hidden rounded-lg border border-border bg-surface">
                {p.url
                  ? <img src={p.url} alt={p.caption || pet.name} className="h-32 w-full object-cover" />
                  : <div className="flex h-32 w-full items-center justify-center text-xs text-ink-faint">anteprima non disponibile</div>}
                <div className="absolute right-1 top-1 flex gap-1">
                  {p.withOwner && <span className="rounded bg-ink/70 p-1 text-white" title="Con il proprietario"><Users className="h-3 w-3" /></span>}
                  {p.storageKey && (
                    <button
                      type="button"
                      className="rounded bg-ink/70 p-1 text-white hover:bg-danger"
                      title="Elimina"
                      onClick={() => deleteMutation.mutate(p.storageKey)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {(p.caption || p.isPrimary) && (
                  <figcaption className="truncate px-2 py-1 text-xs text-ink-muted">
                    {p.isPrimary ? 'Principale - ' : ''}{p.caption || ''}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        ) : (
          <p className="text-sm text-ink-muted">Nessuna foto. Carica la prima foto dell'animale.</p>
        )}

        <section className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-3">
          <p className="text-sm font-medium text-ink">Carica una foto</p>
          <FileInput label="File" accept={ACCEPT} value={file} onChange={setFile} hint="JPEG, PNG, WEBP, GIF o AVIF, max 10 MB." />
          <Input label="Didascalia" value={caption} onChange={(e) => setCaption(e.target.value)} />
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm text-ink-muted">Foto insieme al proprietario</span>
            <Switch checked={withOwner} onChange={setWithOwner} />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm text-ink-muted">Imposta come principale</span>
            <Switch checked={isPrimary} onChange={setIsPrimary} />
          </label>
          <div className="flex justify-end">
            <Button onClick={() => uploadMutation.mutate()} loading={uploadMutation.isPending} disabled={!file}>Carica</Button>
          </div>
        </section>
      </div>
    </Modal>
  );
}
